import { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
// @mui
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  Typography,
  CircularProgress,
  Checkbox,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// form
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  FormProvider,
  RHFTextField,
  RHFSelect,
} from '../../components/hook-form';
// utils
import axios from '../../utils/axios';
// components
import Iconify from '../../components/Iconify';
// assets
import phoneVerificationAnimation from '../../assets/lottie/phone-verification.json';

// Dynamic import Lottie to prevent SSR issues
const Lottie = dynamic(() => import('lottie-react'), {
  ssr: false,
  loading: () => <Box sx={{ width: 200, height: 200 }} />
});

// 화면 단계
const STEPS = {
  FORM: 'form',
  SUBMITTING: 'submitting',
  WAITING_AUTH: 'waitingAuth',
  CONFIRMING: 'confirming',
  SUCCESS: 'success',
  FAILED: 'failed',
};

// 통신사 옵션
const MOBILE_CO_OPTIONS = [
  { value: 'S', label: 'SKT' },
  { value: 'K', label: 'KT' },
  { value: 'L', label: 'LGT' },
];

// 동의 내용
const PRIVACY_CONSENT = {
  title: '개인정보 수집·이용 동의',
  content: `스페이스와이(이하 "회사"라 한다) 건강보험 인증 서비스 이용과 관련하여, 귀하의 개인정보를 수집·이용 하오니, 동의여부를 결정하여 주시기 바랍니다.

1. 개인정보 수집·이용 목적
국민건강보험 자격득실 이력을 통한 경력 검증

2. 수집하려는 개인정보의 항목
- 기본정보: 성명, 생년월일, 휴대전화번호, 통신사, 이용자가 선택한 인증사업자
- 건강보험 가입내역: 가입자구분, 사업장 명칭, 사업자번호, 자격취득일, 자격상실일

3. 개인정보의 보유 및 이용 기간
- 개인정보 수집·이용 목적이 소멸되면 즉시 파기

4. 동의 거부 권리
신청인은 위의 개인정보 수집·이용에 대한 동의를 거부할 권리가 있습니다. 그러나 동의를 거부할 경우 건강보험 인증 서비스 이용이 제한될 수 있습니다.`
};

const THIRD_PARTY_CONSENT = {
  title: '제3자 정보제공 동의',
  content: `스페이스와이(이하 "회사"라 한다) 건강보험 인증 서비스 이용과 관련하여, 귀하의 개인정보를 제공하오니, 동의여부를 결정하여 주시기 바랍니다.

1. 개인정보를 제공받는 자
국민건강보험공단 및 이용자가 선택한 간편인증 사업자

2. 개인정보를 제공받는 자의 개인정보 이용 목적
간편인증 시 본인인증 및 건강보험 자격득실확인서 조회

3. 제공하는 개인정보의 항목
생년월일, 성명, 휴대폰번호, 통신사

4. 개인정보를 제공받는 자의 개인정보 보유 및 이용 기간
본인인증 및 조회 완료 후 즉시 파기

5. 동의 거부 권리
신청인은 위의 개인정보 제3자 제공에 대한 동의를 거부할 권리가 있습니다. 그러나 동의를 거부할 경우 건강보험 인증 서비스 이용이 제한될 수 있습니다.`
};

function PhoneIllustration({ children, badge }) {
  return (
    <Box
      sx={{
        position: 'relative',
        width: 160,
        height: 280,
        my: 2,
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: '24px',
          border: '3px solid',
          borderColor: 'grey.800',
          backgroundColor: 'grey.50',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          overflow: 'hidden',
        }}
      >
        {/* 상단 노치 */}
        <Box
          sx={{
            width: 60,
            height: 6,
            borderRadius: 3,
            backgroundColor: 'grey.800',
            mt: 1.5,
          }}
        />
        {/* 화면 영역 */}
        <Box
          sx={{
            flex: 1,
            width: '85%',
            my: 1.5,
            borderRadius: 1,
            backgroundColor: 'white',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 1.5,
          }}
        >
          {children}
        </Box>
        {/* 하단 바 */}
        <Box
          sx={{
            width: 40,
            height: 4,
            borderRadius: 2,
            backgroundColor: 'grey.800',
            mb: 1,
          }}
        />
      </Box>
      {badge}
    </Box>
  );
}

function getErrorMessage(error) {
  const errorData = error.response?.data;
  return errorData?.error_message || errorData?.detail || '건강보험 인증 요청 중 오류가 발생했습니다';
}

export default function HealthInsuranceVerificationDialog({
  open,
  onClose,
  userName = '',
  onVerified,
  checkVerificationStatus,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [loginOrgs, setLoginOrgs] = useState([]);
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(true);
  const [step, setStep] = useState(STEPS.FORM);
  const [verificationResult, setVerificationResult] = useState(null);
  const [consentModalType, setConsentModalType] = useState(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [verificationId, setVerificationId] = useState(null);
  const [selectedLoginOrgName, setSelectedLoginOrgName] = useState('');

  const isBlocking = step === STEPS.SUBMITTING || step === STEPS.WAITING_AUTH || step === STEPS.CONFIRMING;

  const VerificationSchema = Yup.object().shape({
    name: Yup.string().required('이름을 입력해주세요'),
    birth_date: Yup.string()
      .required('생년월일을 입력해주세요')
      .matches(/^\d{8}$/, '생년월일은 8자리 숫자로 입력해주세요 (예: 19900101)'),
    phone: Yup.string()
      .required('휴대폰 번호를 입력해주세요')
      .matches(/^\d{10,11}$/, '휴대폰 번호는 10-11자리 숫자로 입력해주세요 (하이픈 없이)'),
    mobile_co: Yup.string().required('통신사를 선택해주세요'),
    login_org_cd: Yup.string().required('간편인증 기관을 선택해주세요'),
    privacy_consent: Yup.boolean()
      .oneOf([true], '개인정보 수집·이용에 동의해주세요'),
    third_party_consent: Yup.boolean()
      .oneOf([true], '제3자 정보제공에 동의해주세요'),
  });

  const defaultValues = {
    name: userName || '',
    birth_date: '',
    phone: '',
    mobile_co: '',
    login_org_cd: '',
    privacy_consent: false,
    third_party_consent: false,
  };

  const methods = useForm({
    resolver: yupResolver(VerificationSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    control,
    formState: { errors },
  } = methods;

  const resetToForm = () => {
    setStep(STEPS.FORM);
    setVerificationResult(null);
    setVerificationId(null);
    setSelectedLoginOrgName('');
    reset();
  };

  // 간편인증 기관 목록 조회
  useEffect(() => {
    const fetchLoginOrgs = async () => {
      try {
        setIsLoadingOrgs(true);
        const response = await axios.get('/api/crews/health-insurance/login-orgs/');
        setLoginOrgs(response.data);
      } catch (error) {
        console.error(error);
        enqueueSnackbar('간편인증 기관 목록을 불러오는데 실패했습니다', { variant: 'error' });
      } finally {
        setIsLoadingOrgs(false);
      }
    };

    if (open) {
      fetchLoginOrgs();
      resetToForm();
    }
  }, [open]);

  // 1단계: 인증 요청
  const onSubmit = async (data) => {
    try {
      setStep(STEPS.SUBMITTING);
      const response = await axios.post('/api/crews/health-insurance/verify/', data);

      if (response.data.status === 'pending') {
        setVerificationId(response.data.id);
        const orgName = loginOrgs.find((org) => org.code === data.login_org_cd)?.name || '간편인증 앱';
        setSelectedLoginOrgName(orgName);
        setStep(STEPS.WAITING_AUTH);
      } else {
        // 기존 방식 호환 (즉시 성공/실패)
        setVerificationResult(response.data);
        if (response.data.is_success) {
          enqueueSnackbar('건강보험 인증이 완료되었습니다', { variant: 'success' });
          if (onVerified) {
            onVerified(response.data);
          }
          setStep(STEPS.SUCCESS);
        } else {
          setStep(STEPS.FAILED);
        }
      }
    } catch (error) {
      console.error(error);
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setVerificationResult({ is_success: false, error_message: errorMessage });
      setStep(STEPS.FAILED);
    }
  };

  // 2단계: 인증 확인
  const onConfirm = async () => {
    try {
      setStep(STEPS.CONFIRMING);
      const response = await axios.post('/api/crews/health-insurance/verify/confirm/', {
        verification_id: verificationId,
      });

      setVerificationResult(response.data);

      if (response.data.is_success) {
        enqueueSnackbar('건강보험 인증이 완료되었습니다', { variant: 'success' });
        if (onVerified) {
          onVerified(response.data);
        }
        setStep(STEPS.SUCCESS);
      } else {
        setStep(STEPS.FAILED);
      }
    } catch (error) {
      console.error(error);
      const errorData = error.response?.data;
      const errorMessage = getErrorMessage(error);
      const isExpired = errorData?.error === 'confirm_failed';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setVerificationResult({
        is_success: false,
        error_message: isExpired
          ? '인증 요청이 만료되었습니다. 처음부터 다시 시도해주세요.'
          : errorMessage,
      });
      setStep(STEPS.FAILED);
    }
  };

  const handleClose = () => {
    if (!isBlocking) {
      resetToForm();
      onClose();
    }
  };

  const handleRetry = async () => {
    if (checkVerificationStatus) {
      setIsCheckingStatus(true);
      try {
        const isVerified = await checkVerificationStatus();
        if (isVerified) {
          enqueueSnackbar('건강보험 인증이 이미 완료되었습니다', { variant: 'success' });
          onClose();
          return;
        }
      } catch (error) {
        console.error('상태 체크 실패:', error);
      } finally {
        setIsCheckingStatus(false);
      }
    }
    resetToForm();
  };

  const handleConsentView = (type) => {
    setConsentModalType(type);
  };

  const handleConsentModalClose = () => {
    setConsentModalType(null);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h5">건강보험 인증</Typography>
            {!isBlocking && (
              <Button
                onClick={handleClose}
                sx={{ minWidth: 'auto', p: 1 }}
              >
                <Iconify icon="eva:close-fill" width={24} height={24} />
              </Button>
            )}
          </Stack>
        </DialogTitle>

      <DialogContent>
        {/* 1단계 요청 중 로딩 */}
        {step === STEPS.SUBMITTING && (
          <Box sx={{ pt: 3, pb: 0 }}>
            <Stack spacing={3} alignItems="center">
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h5" gutterBottom>
                  인증 요청 중입니다
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  잠시만 기다려주세요...
                </Typography>
              </Box>

              <Lottie
                animationData={phoneVerificationAnimation}
                loop={true}
                style={{ width: 200, height: 200 }}
              />
            </Stack>
          </Box>
        )}

        {/* 간편인증 대기 화면 - 사용자가 앱에서 인증 후 버튼 클릭 */}
        {step === STEPS.WAITING_AUTH && (
          <Box sx={{ pt: 3, pb: 0 }}>
            <Stack spacing={3} alignItems="center">
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h5" gutterBottom>
                  간편인증을 진행해주세요
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  <strong>{selectedLoginOrgName}</strong>에서 건강보험공단 인증 요청이 도착합니다
                </Typography>
              </Box>

              <PhoneIllustration
                badge={
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: 'error.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: 2,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: 'white', fontWeight: 700, fontSize: '0.7rem' }}>
                      1
                    </Typography>
                  </Box>
                }
              >
                <Iconify
                  icon="eva:shield-fill"
                  sx={{ color: 'primary.main', width: 36, height: 36, mb: 1 }}
                />
                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.65rem', textAlign: 'center' }}>
                  인증 요청
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.55rem', color: 'text.secondary', textAlign: 'center', mt: 0.5 }}>
                  {selectedLoginOrgName}
                </Typography>
                <Box
                  sx={{
                    mt: 1,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    backgroundColor: 'primary.main',
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'white', fontSize: '0.55rem', fontWeight: 600, lineHeight: 1, display: 'block' }}>
                    인증하기
                  </Typography>
                </Box>
              </PhoneIllustration>

              <Box
                sx={{
                  px: 3,
                  py: 2,
                  borderRadius: 1.5,
                  backgroundColor: 'grey.100',
                  width: '100%',
                }}
              >
                <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
                  <Iconify
                    icon="eva:info-fill"
                    sx={{ color: 'text.secondary', width: 16, height: 16, minWidth: 16, minHeight: 16, mt: 0.3 }}
                  />
                  <Typography variant="subtitle2">
                    안내
                  </Typography>
                </Stack>
                <Typography variant="caption" sx={{ display: 'block', ml: 2 }}>
                  • {selectedLoginOrgName} 앱에서 인증을 완료해주세요
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', ml: 2 }}>
                  • 인증 완료 후 아래 &quot;인증완료&quot; 버튼을 눌러주세요
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', ml: 2 }}>
                  • 5분 이내에 인증을 완료하지 않으면 처음부터 다시 진행해야 합니다
                </Typography>
              </Box>

              <Button
                fullWidth
                size="large"
                variant="contained"
                onClick={onConfirm}
                disabled={step !== STEPS.WAITING_AUTH}
              >
                인증완료
              </Button>
            </Stack>
          </Box>
        )}

        {/* 2단계 인증 확인 중 화면 */}
        {step === STEPS.CONFIRMING && (
          <Box sx={{ pt: 3, pb: 0 }}>
            <Stack spacing={3} alignItems="center">
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h5" gutterBottom>
                  간편인증 확인중입니다
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  인증 완료까지 잠시만 기다려주세요
                </Typography>
              </Box>

              <PhoneIllustration>
                <CircularProgress size={40} sx={{ mb: 1.5 }} />
                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.6rem', textAlign: 'center' }}>
                  인증 확인중
                </Typography>
              </PhoneIllustration>

              <Box
                sx={{
                  px: 3,
                  py: 2,
                  borderRadius: 1.5,
                  backgroundColor: 'grey.100',
                  width: '100%',
                }}
              >
                <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
                  <Iconify
                    icon="eva:info-fill"
                    sx={{ color: 'text.secondary', width: 16, height: 16, minWidth: 16, minHeight: 16, mt: 0.3 }}
                  />
                  <Typography variant="subtitle2">
                    안내
                  </Typography>
                </Stack>
                <Typography variant="caption" sx={{ display: 'block', ml: 2 }}>
                  • 인증 결과를 확인하고 있습니다
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', ml: 2 }}>
                  • 완료되면 자동으로 결과 화면으로 이동합니다
                </Typography>
              </Box>
            </Stack>
          </Box>
        )}

        {/* 성공 화면 */}
        {step === STEPS.SUCCESS && verificationResult && (
          <Box>
            <Box
              sx={{
                px: 3,
                py: 2,
                borderRadius: 1.5,
                backgroundColor: 'grey.100',
                mb: 3,
              }}
            >
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <Iconify
                  icon="eva:checkmark-circle-2-fill"
                  sx={{ color: 'success.main', width: 16, height: 16, minWidth: 16, minHeight: 16, mt: 0.3 }}
                />
                <Typography variant="subtitle2">
                  건강보험 인증이 성공적으로 완료되었습니다!
                </Typography>
              </Stack>
            </Box>

            {(verificationResult?.name_mismatch || verificationResult?.phone_mismatch) && (
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  borderRadius: 1.5,
                  backgroundColor: 'grey.100',
                  mb: 3,
                }}
              >
                <Stack direction="row" alignItems="flex-start" spacing={1}>
                  <Iconify
                    icon="eva:alert-triangle-fill"
                    sx={{ color: 'warning.main', width: 16, height: 16, minWidth: 16, minHeight: 16, mt: 0.3 }}
                  />
                  <Typography variant="subtitle2">
                    입력하신 정보가 등록된 정보와 일부 다릅니다. 확인이 필요할 수 있습니다.
                  </Typography>
                </Stack>
              </Box>
            )}

            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  발급일자
                </Typography>
                <Typography variant="body1">
                  {verificationResult?.publication_date?.replace(/(\d{4})(\d{2})(\d{2})/, '$1년 $2월 $3일')}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  발급번호
                </Typography>
                <Typography variant="body1">
                  {verificationResult?.publication_no}
                </Typography>
              </Box>

              {verificationResult?.careers && verificationResult?.careers?.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    경력 이력 ({verificationResult.careers.length}개)
                  </Typography>
                  <Stack spacing={2} sx={{ mt: 1 }}>
                    {verificationResult.careers.map((career, index) => (
                      <Box
                        key={index}
                        sx={{
                          p: 2,
                          borderRadius: 1,
                          backgroundColor: 'grey.100',
                        }}
                      >
                        <Stack spacing={0.5}>
                          <Typography variant="subtitle1">
                            {career.firm_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {career.member_div}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {career.acquired_date?.replace(/(\d{4})(\d{2})(\d{2})/, '$1.$2.$3')}
                            {' ~ '}
                            {career.lose_date
                              ? career.lose_date.replace(/(\d{4})(\d{2})(\d{2})/, '$1.$2.$3')
                              : '재직중'}
                          </Typography>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>

            <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleClose}
              >
                확인
              </Button>
            </Stack>
          </Box>
        )}

        {/* 실패 화면 */}
        {step === STEPS.FAILED && verificationResult && (
          <Box>
            <Box
              sx={{
                px: 3,
                py: 2,
                borderRadius: 1.5,
                backgroundColor: 'grey.100',
                mb: 3,
              }}
            >
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <Iconify
                  icon="eva:close-circle-fill"
                  sx={{ color: 'error.main', width: 16, height: 16, minWidth: 16, minHeight: 16, mt: 0.3 }}
                />
                <Typography variant="subtitle2">
                  {verificationResult?.error_message || '인증에 실패했습니다. 입력하신 정보를 확인해주세요.'}
                </Typography>
              </Stack>
            </Box>

            <Stack direction="row" spacing={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleClose}
              >
                닫기
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={handleRetry}
                disabled={isCheckingStatus}
              >
                {isCheckingStatus ? '확인 중...' : '다시 시도'}
              </Button>
            </Stack>
          </Box>
        )}

        {/* 입력 폼 */}
        {step === STEPS.FORM && (
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={3}>
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  borderRadius: 1.5,
                  backgroundColor: 'grey.100',
                }}
              >
                <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: .5 }}>
                  <Iconify
                    icon="eva:info-fill"
                    sx={{ color: 'text.secondary', width: 16, height: 16, minWidth: 16, minHeight: 16, mt: 0.3 }}
                  />
                  <Typography variant="subtitle2">
                    건강보험 인증 안내
                  </Typography>
                </Stack>
                <ul style={{ marginLeft: "16px" }}>
                  <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                    <Typography variant="caption">
                      건강보험공단 자격득실확인서를 통해 경력을 검증합니다.
                    </Typography>
                  </li>
                  <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                    <Typography variant="caption">
                      휴대폰 간편인증 앱을 통해 진행되며, 본인 인증 후 10-30초 정도 소요될 수 있습니다
                    </Typography>
                  </li>
                </ul>
              </Box>

              <RHFTextField
                name="name"
                label="이름"
                placeholder="홍길동"
              />

              <RHFTextField
                name="birth_date"
                label="생년월일"
                placeholder="19900101"
                helperText="8자리 숫자로 입력해주세요 (예: 19900101)"
              />

              <RHFTextField
                name="phone"
                label="휴대폰 번호"
                placeholder="01012345678"
                helperText="하이픈(-) 없이 입력해주세요"
              />

              <RHFSelect
                name="mobile_co"
                label="통신사"
              >
                <option value="">통신사를 선택해주세요</option>
                {MOBILE_CO_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </RHFSelect>

              {isLoadingOrgs ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <RHFSelect
                  name="login_org_cd"
                  label="간편인증 기관"
                >
                  <option value="">간편인증 기관을 선택해주세요</option>
                  {loginOrgs.map((org) => (
                    <option key={org.code} value={org.code}>
                      {org.name}
                    </option>
                  ))}
                </RHFSelect>
              )}

              {/* 동의 항목 */}
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Controller
                    name="privacy_consent"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        {...field}
                        checked={!!field.value}
                      />
                    )}
                  />
                  <Controller
                    name="privacy_consent"
                    control={control}
                    render={({ field }) => (
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          cursor: 'pointer',
                          userSelect: 'none',
                          '&:hover': {
                            opacity: 0.7
                          }
                        }}
                        onClick={() => field.onChange(!field.value)}
                      >
                        개인정보 수집·이용 동의 (필수)
                      </Typography>
                    )}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleConsentView('privacy')}
                  >
                    보기
                  </Button>
                </Stack>
                {errors.privacy_consent && (
                  <Typography variant="caption" sx={{ color: 'error.main', pl: 5 }}>
                    {errors.privacy_consent.message}
                  </Typography>
                )}

                <Stack direction="row" alignItems="center" spacing={1}>
                  <Controller
                    name="third_party_consent"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        {...field}
                        checked={!!field.value}
                      />
                    )}
                  />
                  <Controller
                    name="third_party_consent"
                    control={control}
                    render={({ field }) => (
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          cursor: 'pointer',
                          userSelect: 'none',
                          '&:hover': {
                            opacity: 0.7
                          }
                        }}
                        onClick={() => field.onChange(!field.value)}
                      >
                        제3자 정보제공 동의 (필수)
                      </Typography>
                    )}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleConsentView('thirdParty')}
                  >
                    보기
                  </Button>
                </Stack>
                {errors.third_party_consent && (
                  <Typography variant="caption" sx={{ color: 'error.main', pl: 5 }}>
                    {errors.third_party_consent.message}
                  </Typography>
                )}
              </Stack>

              <Stack direction="row" spacing={2}>
                <Button
                  fullWidth
                  size="large"
                  variant="outlined"
                  onClick={handleClose}
                >
                  취소
                </Button>
                <LoadingButton
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                  loading={step === STEPS.SUBMITTING}
                >
                  인증요청
                </LoadingButton>
              </Stack>
            </Stack>
          </FormProvider>
        )}
      </DialogContent>
    </Dialog>

    {/* 동의 내용 모달 */}
    <Dialog
      open={!!consentModalType}
      onClose={handleConsentModalClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {consentModalType === 'privacy' ? PRIVACY_CONSENT.title : THIRD_PARTY_CONSENT.title}
          </Typography>
          <Button
            onClick={handleConsentModalClose}
            sx={{ minWidth: 'auto', p: 1 }}
          >
            <Iconify icon="eva:close-fill" width={24} height={24} />
          </Button>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
          {consentModalType === 'privacy' ? PRIVACY_CONSENT.content : THIRD_PARTY_CONSENT.content}
        </Typography>
      </DialogContent>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={handleConsentModalClose}>
          확인
        </Button>
      </Box>
    </Dialog>
    </>
  );
}
